import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { MapPin, Star, Shield, Calendar, Users, ExternalLink, Award, Briefcase, GraduationCap, FileText, Globe, ArrowLeft } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicProfilePage() {
  const { username } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const cleanUsername = username?.replace('@', '');

  useEffect(() => {
    if (!cleanUsername) return;
    // Set SEO meta tags
    document.title = `@${cleanUsername} on Connect`;
    fetchProfile();
  }, [cleanUsername]);

  const fetchProfile = async () => {
    try {
      const res = await fetch(`${API_URL}/api/profile/public/${cleanUsername}`);
      if (res.ok) {
        const data = await res.json();
        setProfile(data);
        // Update meta tags
        document.title = `${data.name} (@${cleanUsername}) — Connect`;
        updateMetaTags(data);
      } else {
        setError('Profile not found');
      }
    } catch {
      setError('Failed to load profile');
    }
    setLoading(false);
  };

  const updateMetaTags = (data) => {
    const setMeta = (name, content) => {
      let el = document.querySelector(`meta[property="${name}"]`) || document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute(name.startsWith('og:') ? 'property' : 'name', name); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };
    const desc = `${data.headline || ''} ${data.bio || ''}`.trim() || `${data.name} on Connect`;
    setMeta('description', desc);
    setMeta('og:title', `${data.name} — Connect`);
    setMeta('og:description', desc);
    setMeta('og:image', data.picture || '');
    setMeta('og:url', window.location.href);
    setMeta('og:type', 'profile');
    setMeta('twitter:card', 'summary');
    setMeta('twitter:title', `${data.name} — Connect`);
    setMeta('twitter:description', desc);
  };

  if (loading) return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (error || !profile) return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center px-6">
      <p className="text-2xl font-bold text-white mb-2">Profile not found</p>
      <p className="text-sm text-zinc-500 mb-6">@{cleanUsername} doesn't exist on Connect</p>
      <Link to="/" className="px-6 py-2.5 bg-white text-black rounded-full text-sm font-bold hover:bg-zinc-200 transition-colors">
        Go to Connect
      </Link>
    </div>
  );

  const hasPeerVerified = (profile.expertise_tree || []).some(b => b.peer_verified);

  return (
    <div className="min-h-screen bg-black text-white" data-testid="public-profile-page">
      {/* Top Nav */}
      <nav className="sticky top-0 z-50 bg-black/90 backdrop-blur-sm border-b border-zinc-800">
        <div className="max-w-2xl mx-auto flex items-center justify-between px-4 py-3">
          <Link to="/" className="flex items-center gap-2 text-white font-extrabold text-lg tracking-tight">
            <ArrowLeft className="w-5 h-5" />
            Connect
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/signup" className="px-4 py-1.5 bg-white text-black text-xs font-bold rounded-full hover:bg-zinc-200 transition-colors" data-testid="public-signup-btn">
              Join Connect
            </Link>
          </div>
        </div>
      </nav>

      <div className="max-w-2xl mx-auto">
        {/* Cover + Avatar */}
        <div className="relative">
          {profile.cover_photo ? (
            <div className="h-44 bg-zinc-900 overflow-hidden">
              <img src={profile.cover_photo} alt="" className="w-full h-full object-cover" />
            </div>
          ) : (
            <div className="h-44 bg-gradient-to-br from-purple-900/40 to-zinc-900" />
          )}
          <div className="absolute -bottom-12 left-4">
            <div className={`w-24 h-24 rounded-full border-4 border-black p-[2px] ${hasPeerVerified ? 'bg-gradient-to-br from-yellow-400 to-orange-500' : 'bg-gradient-to-br from-purple-500 to-orange-400'}`}>
              <div className="w-full h-full rounded-full bg-black flex items-center justify-center overflow-hidden">
                {profile.picture ? <img src={profile.picture} alt={profile.name} className="w-full h-full object-cover" /> :
                  <span className="text-2xl font-bold">{profile.name?.[0]}</span>}
              </div>
            </div>
          </div>
        </div>

        {/* Profile Info */}
        <div className="pt-14 px-4 pb-4 border-b border-zinc-800">
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-extrabold">{profile.name}</h1>
            {profile.is_blue_verified && (
              <svg className="w-5 h-5 text-blue-500 shrink-0" viewBox="0 0 22 22" fill="currentColor" data-testid="public-blue-tick">
                <path d="M20.396 11c-.018-.646-.215-1.275-.57-1.816-.354-.54-.852-.972-1.438-1.246.223-.607.27-1.264.14-1.897-.131-.634-.437-1.218-.882-1.687-.47-.445-1.053-.75-1.687-.882-.633-.13-1.29-.083-1.897.14-.273-.587-.704-1.086-1.245-1.44S11.647 1.62 11 1.604c-.646.017-1.273.213-1.813.568s-.969.855-1.24 1.44c-.608-.223-1.267-.272-1.902-.14-.635.13-1.22.436-1.69.882-.445.47-.749 1.055-.878 1.69-.13.633-.08 1.29.144 1.896-.587.274-1.087.705-1.443 1.245-.356.54-.555 1.17-.574 1.817.02.647.218 1.276.574 1.817.356.54.856.972 1.443 1.245-.224.606-.274 1.263-.144 1.896.13.636.433 1.221.878 1.69.47.446 1.055.752 1.69.883.635.13 1.294.083 1.902-.141.27.587.7 1.086 1.24 1.44s1.167.551 1.813.568c.645-.016 1.276-.213 1.817-.567s.972-.854 1.245-1.44c.604.224 1.26.273 1.896.143.636-.13 1.22-.436 1.69-.88.445-.47.75-1.055.88-1.69.13-.634.08-1.29-.14-1.898.587-.273 1.084-.704 1.438-1.244.355-.54.552-1.17.57-1.817zM9.662 14.85l-3.429-3.428 1.293-1.302 2.072 2.072 4.4-4.794 1.347 1.246z"/>
              </svg>
            )}
            {hasPeerVerified && !profile.is_blue_verified && <Shield className="w-5 h-5 text-yellow-400" />}
          </div>
          <p className="text-sm text-zinc-400">@{cleanUsername}</p>
          {profile.headline && <p className="text-sm text-zinc-300 mt-1">{profile.headline}</p>}
          {(profile.position || profile.company) && (
            <p className="text-xs text-zinc-400 mt-0.5 flex items-center gap-1">
              <Briefcase className="w-3 h-3" />
              {profile.position}{profile.position && profile.company ? ' at ' : ''}{profile.company}
            </p>
          )}
          {profile.bio && <p className="text-sm text-zinc-300 mt-2 leading-relaxed">{profile.bio}</p>}

          {/* Meta line */}
          <div className="flex flex-wrap items-center gap-3 mt-3 text-xs text-zinc-500">
            {profile.city && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{profile.city}{profile.country ? `, ${profile.country}` : ''}</span>}
            {profile.rating > 0 && <span className="flex items-center gap-1"><Star className="w-3 h-3 text-yellow-400" />{profile.rating.toFixed(1)}</span>}
            {profile.education && <span className="flex items-center gap-1"><GraduationCap className="w-3 h-3" />{profile.education}</span>}
            {profile.website && <a href={profile.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-purple-400 hover:underline"><Globe className="w-3 h-3" />Website</a>}
          </div>

          {/* Stats */}
          <div className="flex items-center gap-6 mt-3">
            <div className="text-center"><p className="font-bold text-lg">{profile.connected_count || 0}</p><p className="text-[10px] text-zinc-500">Connected</p></div>
            <div className="text-center"><p className="font-bold text-lg">{profile.connecting_count || 0}</p><p className="text-[10px] text-zinc-500">Connecting</p></div>
            {profile.community && <div className="text-center"><p className="font-bold text-lg">{profile.community.member_count || 0}</p><p className="text-[10px] text-zinc-500">Community</p></div>}
          </div>

          {/* CTA */}
          <div className="flex items-center gap-2 mt-4">
            <Link to={`/book/${profile.user_id}`}
              className="flex-1 py-2.5 bg-white text-black text-sm font-bold rounded-xl text-center hover:bg-zinc-200 transition-colors flex items-center justify-center gap-1.5"
              data-testid="public-schedule-btn">
              <Calendar className="w-4 h-4" /> Schedule a Call
            </Link>
            <button onClick={() => { navigator.clipboard.writeText(window.location.href); }}
              className="px-4 py-2.5 bg-zinc-800 border border-zinc-700 text-white text-sm font-bold rounded-xl hover:bg-zinc-700 transition-colors"
              data-testid="public-share-btn">
              <ExternalLink className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Expertise */}
        {(profile.expertise_tree || []).length > 0 && (
          <div className="px-4 py-4 border-b border-zinc-800">
            <h2 className="text-sm font-bold uppercase tracking-wide text-zinc-400 mb-3">Expertise</h2>
            <div className="flex flex-wrap gap-2">
              {profile.expertise_tree.map((b, i) => (
                <span key={i} className={`text-xs px-3 py-1.5 rounded-full font-medium ${b.peer_verified ? 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20' : b.verified ? 'bg-yellow-500/10 text-yellow-400' : 'bg-zinc-800 text-zinc-300'}`}>
                  {b.expertise || b.industry}
                  {b.skill_level === 'expert' && ' (Expert)'}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Open To Tags */}
        {profile.open_to?.length > 0 && (
          <div className="px-4 py-4 border-b border-zinc-800">
            <h2 className="text-sm font-bold uppercase tracking-wide text-zinc-400 mb-3">Open To</h2>
            <div className="flex flex-wrap gap-2">
              {profile.open_to.map((tag, i) => (
                <span key={i} className="text-xs px-3 py-1.5 rounded-full bg-green-500/10 text-green-400 border border-green-500/20">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Certificates */}
        {profile.certificates?.length > 0 && (
          <div className="px-4 py-4 border-b border-zinc-800">
            <h2 className="text-sm font-bold uppercase tracking-wide text-zinc-400 mb-3">Certificates</h2>
            <div className="grid grid-cols-2 gap-2">
              {profile.certificates.map((c, i) => (
                <div key={i} className="bg-zinc-900 border border-zinc-800 rounded-xl p-3 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-purple-400 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-zinc-300 truncate">{c.name || 'Certificate'}</p>
                    {c.issuer && <p className="text-[9px] text-zinc-500">{c.issuer}</p>}
                  </div>
                  {c.verified && <Shield className="w-3 h-3 text-green-400 shrink-0" />}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Work Experience */}
        {profile.experiences?.length > 0 && (
          <div className="px-4 py-4 border-b border-zinc-800">
            <h2 className="text-sm font-bold uppercase tracking-wide text-zinc-400 mb-3">Experience</h2>
            {profile.experiences.map((exp, i) => (
              <div key={i} className="mb-3 last:mb-0">
                <p className="text-sm font-semibold">{exp.title}</p>
                {exp.company && <p className="text-xs text-zinc-400">{exp.company}</p>}
                {exp.start_date && <p className="text-[10px] text-zinc-500">{exp.start_date} — {exp.end_date || 'Present'}</p>}
                {exp.description && <p className="text-xs text-zinc-400 mt-1">{exp.description}</p>}
              </div>
            ))}
          </div>
        )}

        {/* Education */}
        {profile.education_history?.length > 0 && (
          <div className="px-4 py-4 border-b border-zinc-800">
            <h2 className="text-sm font-bold uppercase tracking-wide text-zinc-400 mb-3">Education</h2>
            {profile.education_history.map((edu, i) => (
              <div key={i} className="mb-3 last:mb-0">
                <p className="text-sm font-semibold">{edu.institution}</p>
                <p className="text-xs text-zinc-400">{edu.degree}{edu.field ? ` — ${edu.field}` : ''}</p>
                {edu.start_year && <p className="text-[10px] text-zinc-500">{edu.start_year} — {edu.end_year || 'Present'}</p>}
              </div>
            ))}
          </div>
        )}

        {/* Recent Posts */}
        {profile.recent_posts?.length > 0 && (
          <div className="px-4 py-4 border-b border-zinc-800">
            <h2 className="text-sm font-bold uppercase tracking-wide text-zinc-400 mb-3">Recent Posts</h2>
            {profile.recent_posts.map((p, i) => (
              <div key={i} className="bg-zinc-900 border border-zinc-800 rounded-xl p-3 mb-2">
                <p className="text-sm text-zinc-300 line-clamp-3">{p.content}</p>
                <div className="flex items-center gap-3 mt-2 text-[10px] text-zinc-500">
                  <span>{p.likes_count || 0} likes</span>
                  <span>{p.comments_count || 0} comments</span>
                  <span>{new Date(p.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Upcoming Group Sessions */}
        {profile.upcoming_events?.length > 0 && (
          <div className="px-4 py-4 border-b border-zinc-800">
            <h2 className="text-sm font-bold uppercase tracking-wide text-zinc-400 mb-3">Upcoming Sessions</h2>
            {profile.upcoming_events.map((evt, i) => (
              <div key={i} className="bg-zinc-900 border border-zinc-800 rounded-xl p-3 mb-2">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-white">{evt.title}</p>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${evt.price > 0 ? 'bg-emerald-500/15 text-emerald-400' : 'bg-zinc-700 text-zinc-300'}`}>
                    {evt.price > 0 ? `₹${evt.price}` : 'Free'}
                  </span>
                </div>
                <p className="text-[10px] text-zinc-500 mt-1">{evt.date} · {evt.duration_minutes}min</p>
              </div>
            ))}
          </div>
        )}

        {/* Footer CTA */}
        <div className="px-4 py-8 text-center">
          <p className="text-sm text-zinc-500 mb-4">Want to connect with {profile.name?.split(' ')[0]}?</p>
          <Link to="/signup" className="inline-block px-8 py-3 bg-white text-black font-bold rounded-full text-sm hover:bg-zinc-200 transition-colors" data-testid="public-footer-signup">
            Join Connect for Free
          </Link>
        </div>
      </div>
    </div>
  );
}
